import React, { useState } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

interface InlinePaymentFormProps {
  amountCents: number;
  currency: string;
  customerEmail?: string;
  onPaymentSuccess: () => void;
}

export function InlinePaymentForm({ amountCents, currency, customerEmail, onPaymentSuccess }: InlinePaymentFormProps): JSX.Element {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const amountDisplay = (amountCents / 100).toFixed(2);
  const currencySymbol = currency.toUpperCase() === 'USD' ? '$' : currency.toUpperCase();

  // Check if running on valid protocol
  const isValidProtocol = typeof window !== 'undefined' &&
    (window.location.protocol === 'http:' || window.location.protocol === 'https:');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      // Build confirm options - only include return_url for valid protocols
      const confirmOptions: any = {
        elements,
        redirect: 'if_required',
      };

      if (isValidProtocol) {
        confirmOptions.confirmParams = {
          return_url: window.location.href,
        };
      }

      const { error, paymentIntent } = await stripe.confirmPayment(confirmOptions);

      if (error) {
        console.error('Stripe payment error:', error);
        setErrorMessage(error.message || 'Payment failed. Please try again.');
        setIsProcessing(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onPaymentSuccess();
        setIsProcessing(false);
      } else {
        // Payment is processing or requires additional action
        console.log('Payment status:', paymentIntent?.status);
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Unexpected error during payment:', err);
      setErrorMessage(err instanceof Error ? err.message : 'An unexpected error occurred.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="cc-payment-form">
      <div className="cc-payment-form__header">
        <svg className="cc-payment-form__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
          <line x1="1" y1="10" x2="23" y2="10" />
        </svg>
        <span className="cc-payment-form__title">Complete Payment</span>
      </div>

      <div className="cc-payment-form__amount">
        {currencySymbol}{amountDisplay}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="cc-payment-form__element">
          <PaymentElement options={customerEmail ? {
            defaultValues: { billingDetails: { email: customerEmail } },
          } : undefined} />
        </div>

        {errorMessage && (
          <div className="cc-payment-form__error" role="alert">
            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          className="cc-btn cc-btn--primary cc-btn--block cc-payment-form__submit"
          disabled={!stripe || isProcessing}
        >
          {isProcessing ? (
            <>
              <span className="cc-spinner" />
              Processing...
            </>
          ) : (
            `Pay ${currencySymbol}${amountDisplay}`
          )}
        </button>
      </form>
    </div>
  );
}
